/**
 * Input Node Data Resolver
 *
 * Extracts input data from connected nodes based on edges and handle configurations.
 * Supports single and multiple connections per handle.
 */

import type { Node, Edge } from '@xyflow/react';
import type { HandleMeta } from '../types';
import type { GenieNodeState } from '@genieartinc/schemas';

/** Node data shape when stored in React Flow's data field */
interface GenieFlowNodeData extends Record<string, unknown> {
  state?: GenieNodeState;
  handles?: HandleMeta[];
}

/** Typed accessor for Genie node data from a React Flow Node */
function getNodeData(node: Node): GenieFlowNodeData {
  return (node.data || {}) as GenieFlowNodeData;
}

/** Extract state from node data, checking both data.state and component.state patterns */
function getNodeState(node: Node): GenieNodeState | undefined {
  const data = getNodeData(node);
  return data.state || (data as Record<string, unknown>).component
    ? ((data as Record<string, unknown>).component as { state?: GenieNodeState } | undefined)?.state
    : undefined;
}

/**
 * Input data structure for generation
 */
export interface InputNode {
  prompt?: string;
  systemPrompt?: string;
  image?: string | string[];  // Single or array based on connections
  images?: string[];          // Explicitly multi-image
  video?: string;
  mask?: string;
  code?: string;
  [key: string]: string | string[] | undefined;  // Allow custom parameters
}

/**
 * Extract input data from connected nodes
 *
 * @param nodeId - Target node ID requesting inputs
 * @param nodes - All canvas nodes (from store)
 * @param edges - All canvas edges (from store)
 * @returns Mapped input data by parameter name
 *
 * @example
 * const inputData = getInputNode('node-123', nodes, edges);
 * // { prompt: "sunset", image: "https://..." }
 */
export function getInputNode(
  nodeId: string,
  nodes: Node[],
  edges: Edge[]
): InputNode {
  const targetNode = nodes.find(n => n.id === nodeId);
  if (!targetNode) {
    console.warn(`[getInputNode] Node not found: ${nodeId}`);
    return {};
  }

  const inputEdges = edges.filter(e => e.target === nodeId);
  if (inputEdges.length === 0) {
    // No connections - return empty (will use internal state)
    return {};
  }

  const inputData: InputNode = {};

  // Group edges by target handle
  const edgesByHandle = groupEdgesByHandle(inputEdges);

  // Process each handle
  for (const [handleId, handleEdges] of edgesByHandle) {
    const handle = getNodeData(targetNode).handles?.find((h: HandleMeta) => h.id === handleId);

    if (handle) {
      // Handle metadata found - use typed data extraction
      const values = collectHandleValues(handleEdges, handle, nodes);
      if (values.length === 0) continue;

      const paramName = mapHandleToParameter(handleId, handle.dataType ?? 'any', handle);
      const isSingleValueHandle = handle.maxConnections === 1;
      inputData[paramName] = isSingleValueHandle && values.length === 1
        ? values[0]
        : values;
    } else {
      // No handle metadata (generic React Flow handles) - infer from source node
      for (const edge of handleEdges) {
        const sourceNode = nodes.find(n => n.id === edge.source);
        if (!sourceNode) continue;

        const sourceState = getNodeState(sourceNode);
        if (!sourceState) continue;

        // Infer data type from source node type and extract accordingly
        const sourceType = sourceNode.type || '';

        if (sourceType === 'TextFile' || sourceType === 'TextNode') {
          // Text source → map to prompt
          const text = sourceState.prompt || sourceState.createdItems?.[0]?.content;
          if (text && typeof text === 'string') inputData.prompt = text;
        } else if (sourceType === 'ShaderNode') {
          const capture = latestImageItem(sourceState.createdItems);
          if (capture && typeof capture.content === 'string') inputData.image = capture.content;
        } else if (sourceType === 'ImageFile' || sourceType === 'ImageNode') {
          // Image source → map to image
          const fileMetadata = (sourceState as Record<string, unknown>).fileMetadata as { publicUrl?: string } | undefined;
          const image = fileMetadata?.publicUrl
            || (typeof sourceState.createdItems?.[0]?.content === 'string' ? sourceState.createdItems[0].content : undefined)
            || (typeof sourceState.images?.[0] === 'string' ? sourceState.images[0] : undefined);
          if (image) inputData.image = image;
        } else {
          // Unknown source - try text first, then image
          const fileMetadata = (sourceState as Record<string, unknown>).fileMetadata as { publicUrl?: string } | undefined;
          if (sourceState.prompt) inputData.prompt = sourceState.prompt;
          else if (fileMetadata?.publicUrl) inputData.image = fileMetadata.publicUrl;
        }
      }
    }
  }

  return inputData;
}

/** Newest image entry in a node's history (histories that append keep it last). */
function latestImageItem<T extends { type?: string }>(items: T[] | undefined): T | undefined {
  if (!items) return undefined;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i]?.type === 'image') return items[i];
  }
  return undefined;
}

/**
 * Group edges by target handle ID
 */
function groupEdgesByHandle(edges: Edge[]): Map<string, Edge[]> {
  const map = new Map<string, Edge[]>();

  for (const edge of edges) {
    const handleId = edge.targetHandle || 'default';
    if (!map.has(handleId)) {
      map.set(handleId, []);
    }
    map.get(handleId)!.push(edge);
  }

  return map;
}

/**
 * Collect values from all connected nodes (respecting maxConnections limit)
 */
function collectHandleValues(
  handleEdges: Edge[],
  handle: HandleMeta,
  nodes: Node[]
): string[] {
  const values: string[] = [];
  const limit = handle.maxConnections || Infinity;

  for (const edge of handleEdges) {
    if (values.length >= limit) break; // Respect limit

    const sourceNode = nodes.find(n => n.id === edge.source);
    if (!sourceNode) continue;

    const data = extractNode(sourceNode, handle.dataType ?? 'any');
    if (data !== null && data !== undefined) {
      values.push(data);
    }
  }

  return values;
}

/**
 * Extract data from a source node based on data type
 */
function extractNode(node: Node, dataType: string): string | null {
  const state = getNodeState(node);
  if (!state) return null;

  // Access fileMetadata via passthrough (GenieNodeState uses .passthrough())
  const fileMetadata = (state as Record<string, unknown>).fileMetadata as { publicUrl?: string } | undefined;
  // Access code field via passthrough
  const code = (state as Record<string, unknown>).code as string | undefined;

  /** Helper to extract string content from a CreatedItem */
  const getItemContent = (item: { content: string | File | Blob }): string | null =>
    typeof item.content === 'string' ? item.content : null;

  switch (dataType) {
    case 'text':
      // Text nodes: use prompt or first createdItem
      if (state.prompt) return state.prompt;
      if (state.createdItems?.[0]?.type === 'text') {
        return getItemContent(state.createdItems[0]);
      }
      return null;

    case 'image': {
      // Shader nodes append captures, so the newest one is last.
      if (node.type === 'ShaderNode') {
        const capture = latestImageItem(state.createdItems);
        return capture ? getItemContent(capture) : null;
      }
      // Image nodes: use first createdItem or first image
      if (state.createdItems?.[0]?.type === 'image') {
        return getItemContent(state.createdItems[0]);
      }
      const firstImage = state.images?.[0];
      if (typeof firstImage === 'string') return firstImage;
      // ImageFile nodes: use fileMetadata.publicUrl
      if (fileMetadata?.publicUrl) return fileMetadata.publicUrl;
      return null;
    }

    case 'video':
      // Video nodes: use first createdItem or video field
      if (state.createdItems?.[0]?.type === 'video') {
        return getItemContent(state.createdItems[0]);
      }
      if (typeof state.video === 'string') return state.video;
      return null;

    case 'code':
      // Code nodes: use code field from passthrough or first createdItem with code-like content
      if (code) return code;
      // CreatedItem.type uses NodeType schema which doesn't include 'code' directly;
      // shader nodes produce code content, so check for shader type as fallback
      if (state.createdItems?.[0]?.type === 'shader') {
        return getItemContent(state.createdItems[0]);
      }
      return null;

    case 'mask': {
      const maskData = (state as Record<string, unknown>).maskData as string | undefined;
      if (maskData) return maskData;
      return null;
    }

    case 'any':
    default: {
      // Try common fields
      if (state.prompt) return state.prompt;
      if (state.createdItems?.[0]) {
        return getItemContent(state.createdItems[0]);
      }
      const firstImg = state.images?.[0];
      if (typeof firstImg === 'string') return firstImg;
      if (typeof state.video === 'string') return state.video;
      if (fileMetadata?.publicUrl) return fileMetadata.publicUrl;
      return null;
    }
  }
}

/**
 * Map handle ID to generation parameter name
 */
function mapHandleToParameter(handleId: string, dataType: string, handle?: HandleMeta): string {
  // Level 0: Check handle metadata for explicit parameter name
  if (handle?.metadata?.parameterName) {
    return handle.metadata.parameterName;
  }

  // Direct mappings
  const directMappings: Record<string, string> = {
    'prompt-input': 'prompt',
    'system-prompt-input': 'systemPrompt',
    'image-input': 'image',
    'images-input': 'images',
    'mask-input': 'mask',
    'video-input': 'video',
    'code-input': 'code',
    'text-output': 'prompt',
    'image-output': 'image',
    'video-output': 'video',
  };

  if (directMappings[handleId]) {
    return directMappings[handleId];
  }

  // Pattern matching
  const lowerHandleId = handleId.toLowerCase();

  if (lowerHandleId.includes('prompt')) return 'prompt';
  if (lowerHandleId.includes('system')) return 'systemPrompt';
  if (lowerHandleId.includes('mask')) return 'mask';
  if (lowerHandleId.includes('code')) return 'code';

  // Multi-image patterns
  if (lowerHandleId.includes('images') || lowerHandleId === 'image-input-multi') {
    return 'images';
  }

  // Single image
  if (lowerHandleId.includes('image')) return 'image';
  if (lowerHandleId.includes('video')) return 'video';

  // Fallback: use dataType as parameter name
  return dataType;
}

